import {
  IQuoteSettingsConfigSchema,
  SigningOrderEnum,
} from '@monetize/types/app';
import {
  AsyncStatusIndicator,
  MBox,
  MFlex,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { useAsyncStatus } from '../../../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../../hooks/useSettingsSectionHighlight';
import { SettingsSection } from '../../../components/SettingsSection';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';

interface QuoteEsignSettingsSigningOrderProps {
  quoteSettings: IQuoteSettingsConfigSchema;
  onSubmit: (data: Partial<IQuoteSettingsConfigSchema>) => Promise<void>;
}

export const QuoteEsignSettingsSigningOrder = ({
  quoteSettings,
  onSubmit,
}: QuoteEsignSettingsSigningOrderProps) => {
  const isOrderHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteEsignSigningOrder,
  );
  const { status: signingOrderStatus, track: trackSigningOrderSave } =
    useAsyncStatus();

  const handleSigningOrderChange = (signingOrder: SigningOrderEnum) => {
    if (signingOrder === quoteSettings.signingOrder) {
      return;
    }
    trackSigningOrderSave(() => onSubmit({ signingOrder }));
  };

  const signingOrder =
    quoteSettings.signingOrder || SigningOrderEnum.EXTERNAL_FIRST;

  return (
    <SettingsSection
      label="Default Signing Order"
      id={QUOTE_SETTING_KEYS.quoteEsignSigningOrder}
      {...getHighlightStyles(isOrderHighlighted)}
      showDivider={false}
    >
      <MFlex align="center" mb={2}>
        <MText mr={2}>
          Select the default signing order applied to newly created quotes.
        </MText>
        <MBox position="relative">
          <AsyncStatusIndicator status={signingOrderStatus} />
        </MBox>
      </MFlex>
      <MRadioGroup
        value={signingOrder}
        onChange={(value) =>
          handleSigningOrderChange(value as SigningOrderEnum)
        }
        isDisabled={signingOrderStatus === 'loading'}
      >
        <MStack rowGap={2} display="flex" flexDir="column">
          <MRadio value={SigningOrderEnum.EXTERNAL_FIRST}>
            Send to <strong>customer</strong> first
          </MRadio>
          <MRadio value={SigningOrderEnum.INTERNAL_FIRST}>
            Send to <strong>internal signer</strong> first
          </MRadio>
          <MRadio value={SigningOrderEnum.INSTANTANEOUS}>
            Send to <strong>customer and internal signer</strong> at the same
            time
          </MRadio>
          <MRadio value={SigningOrderEnum.EXTERNAL_ONLY}>
            Only require signature from <strong>customer</strong>
          </MRadio>
        </MStack>
      </MRadioGroup>
    </SettingsSection>
  );
};
